// Hooks utilitários de UI compartilhados.
import { useEffect, useRef, useState, type RefObject } from "react";
import { registrarVoltar } from "@plataforma/voltar";

/** true depois de `atrasoMs` com `ativo` ligado (esqueleto só após 300 ms — DESIGN §5.14). */
export function useAtraso(ativo: boolean, atrasoMs = 300): boolean {
  const [passou, setPassou] = useState(false);
  useEffect(() => {
    if (!ativo) {
      setPassou(false);
      return;
    }
    const t = window.setTimeout(() => setPassou(true), atrasoMs);
    return () => window.clearTimeout(t);
  }, [ativo, atrasoMs]);
  return passou;
}

/** Media query reativa. Ex.: useMedia("(min-width: 1024px)") para o painel lg. */
export function useMedia(consulta: string): boolean {
  const [casa, setCasa] = useState(
    () => typeof window !== "undefined" && window.matchMedia(consulta).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(consulta);
    const aoMudar = () => setCasa(mql.matches);
    aoMudar();
    mql.addEventListener("change", aoMudar);
    return () => mql.removeEventListener("change", aoMudar);
  }, [consulta]);
  return casa;
}

export const useDesktop = () => useMedia("(min-width: 1024px)");

/** Online/offline do navegador. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Foco preso dentro de um diálogo (folha, modal, gaveta): foca o primeiro controle ao abrir,
 * Tab circula dentro, Esc chama `aoFechar`, e ao fechar devolve o foco para quem abriu.
 * No app nativo, o voltar do Android faz o mesmo que o Esc: fecha esta camada antes de voltar a tela (sem
 * `aoFechar`, o voltar não faz nada enquanto ela estiver aberta). No web o trecho nem existe (`__ALVO__`).
 */
export function useFocoPreso(
  ref: RefObject<HTMLElement | null>,
  aberto: boolean,
  aoFechar?: () => void,
): void {
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;

  useEffect(() => {
    if (!aberto) return;
    const anterior = document.activeElement as HTMLElement | null;
    const raiz = ref.current;
    const primeiro =
      raiz?.querySelector<HTMLElement>("[data-autofoco]") ?? raiz?.querySelector<HTMLElement>(FOCAVEIS);
    (primeiro ?? raiz)?.focus();

    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        aoFecharRef.current?.();
        return;
      }
      if (e.key !== "Tab" || !raiz) return;
      const itens = Array.from(raiz.querySelectorAll<HTMLElement>(FOCAVEIS)).filter(
        (el) => el.offsetParent !== null,
      );
      if (itens.length === 0) return;
      const inicio = itens[0]!;
      const fim = itens[itens.length - 1]!;
      if (e.shiftKey && document.activeElement === inicio) {
        e.preventDefault();
        fim.focus();
      } else if (!e.shiftKey && document.activeElement === fim) {
        e.preventDefault();
        inicio.focus();
      }
    }
    document.addEventListener("keydown", aoTeclar);
    let soltarVoltar: (() => void) | undefined;
    if (__ALVO__ === "nativo") soltarVoltar = registrarVoltar(() => aoFecharRef.current?.());
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      if (__ALVO__ === "nativo") soltarVoltar?.();
      anterior?.focus?.();
    };
  }, [aberto, ref]);
}

/** Trava a rolagem do body enquanto `ativo` (folha/modal abertos). */
// Contagem de camadas abertas (gaveta + "Descartar alterações?" por cima, modal dentro de folha...). Guardar e
// restaurar o valor "de antes" em cada camada quebra quando elas fecham juntas: o React desmonta a de baixo
// primeiro, a de cima restaura "hidden" e a página fica sem rolar até o F5 (bug do "Ativar" com e-mail em uso).
let travasDeRolagem = 0;

export function useTravarRolagem(ativo: boolean): void {
  useEffect(() => {
    if (!ativo) return;
    travasDeRolagem += 1;
    document.body.style.overflow = "hidden";
    return () => {
      travasDeRolagem = Math.max(0, travasDeRolagem - 1);
      if (travasDeRolagem === 0) document.body.style.overflow = "";
    };
  }, [ativo]);
}

/** Troca o `theme-color` enquanto montado (telas cheias em tinta — DESIGN §3.1). */
export function useCorDoTema(cor: string, ativo = true): void {
  useEffect(() => {
    if (!ativo) return;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    const antes = meta.content;
    meta.content = cor;
    return () => {
      meta.content = antes;
    };
  }, [cor, ativo]);
}
